import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../../helpers'
import { useLeads } from '../../../hooks/Outreach/useLeads'
import { Lead } from '../../../models/Outreach/types'
import { InviteToApplyDialog } from '../LeadsPipeline/InviteToApplyDialog'
import { useDeleteLead } from '../../../hooks/Outreach/useLeadCrm'
import { PersonFileDialog } from '../LeadsPipeline/LeadRecordDrawer'
import { mapLeadToPipelineRecord } from '../LeadsPipeline/mapLeadToPipelineRecord'
import { ScheduleTourDialog } from '../LeadsPipeline/ScheduleTourDialog'
import { SendLeaseOfferDialog } from '../LeadsPipeline/SendLeaseOfferDialog'
import { FindPersonDialog } from './FindPersonDialog'
import { PersonFileTarget } from './types'

interface PersonFileContextValue {
    openPersonFile: (target: PersonFileTarget) => void
    closePersonFile: () => void
    openFindPerson: () => void
}

const PersonFileContext = createContext<PersonFileContextValue | null>(null)

export function usePersonFile(): PersonFileContextValue {
    const value = useContext(PersonFileContext)
    if (!value) {
        throw new Error('usePersonFile must be used within PersonFileProvider')
    }
    return value
}

interface PersonFileProviderProps {
    apartmentId: number | undefined
    seasonId: number | null | undefined
    readOnly?: boolean
    children: React.ReactNode
}

export const PersonFileProvider: React.FC<PersonFileProviderProps> = ({
    apartmentId,
    seasonId,
    readOnly = false,
    children,
}) => {
    const queryClient = useQueryClient()
    const [target, setTarget] = useState<PersonFileTarget | null>(null)
    const [findOpen, setFindOpen] = useState(false)
    const [inviteLead, setInviteLead] = useState<Lead | null>(null)
    const [tourLead, setTourLead] = useState<Lead | null>(null)
    const [editTourId, setEditTourId] = useState<number | null>(null)
    const [offerLead, setOfferLead] = useState<Lead | null>(null)

    const leadId = target?.leadId ?? target?.lead?.id ?? null

    const leadQuery = useQuery<Lead>({
        queryKey: ['person-file-lead', leadId],
        queryFn: async () => {
            const res = await axiosInstance.get<Lead>(
                `/outreach/leads/${leadId}/`,
            )
            return res.data
        },
        enabled: leadId != null,
        refetchOnWindowFocus: false,
    })

    const lead = leadQuery.data ?? target?.lead ?? null

    const {
        startScreening,
        isStartingScreening,
        resendScreeningInvitation,
        isResendingScreeningInvitation,
        sendLeasePackage,
        isSendingLeasePackage,
    } = useLeads({
        apartmentId,
        filters: { is_lead: true, page_size: 1 },
        enabled: false,
    })
    const { deleteLead, isDeleting: isDeletingLead } = useDeleteLead()

    const refreshLead = useCallback(() => {
        if (leadId != null) {
            queryClient.invalidateQueries({
                queryKey: ['person-file-lead', leadId],
            })
        }
        queryClient.invalidateQueries({ queryKey: ['leads'], exact: false })
        queryClient.invalidateQueries({
            queryKey: ['leasing-season-pipeline-counts'],
            exact: false,
        })
        queryClient.invalidateQueries({
            queryKey: ['renewal-offers'],
            exact: false,
        })
    }, [leadId, queryClient])

    const openPersonFile = useCallback((next: PersonFileTarget) => {
        setFindOpen(false)
        setTarget(next)
    }, [])

    const closePersonFile = useCallback(() => {
        setTarget(null)
    }, [])

    const openFindPerson = useCallback(() => {
        setFindOpen(true)
    }, [])

    const handleStartScreening = useCallback(
        async (id: number) => {
            await startScreening(id)
            refreshLead()
        },
        [startScreening, refreshLead],
    )

    const handleResendScreening = useCallback(
        async (id: number) => {
            const result = await resendScreeningInvitation(id)
            if (result.invitation_url) {
                try {
                    await navigator.clipboard.writeText(result.invitation_url)
                    toast.info('Invite URL copied to clipboard')
                } catch {
                    // Clipboard may be unavailable
                }
            }
            refreshLead()
        },
        [resendScreeningInvitation, refreshLead],
    )

    const handleSendLeasePackage = useCallback(
        async (id: number) => {
            await sendLeasePackage(id)
            refreshLead()
        },
        [sendLeasePackage, refreshLead],
    )

    const handleDeleteLead = useCallback(
        async (id: number) => {
            await deleteLead(id)
            closePersonFile()
        },
        [deleteLead, closePersonFile],
    )

    const offerRecord = offerLead ? mapLeadToPipelineRecord(offerLead) : null

    const value = useMemo(
        () => ({
            openPersonFile,
            closePersonFile,
            openFindPerson,
        }),
        [openPersonFile, closePersonFile, openFindPerson],
    )

    const fileOpen =
        target != null &&
        (lead != null ||
            target.prospect != null ||
            target.renewalOffer != null ||
            leadQuery.isLoading)

    return (
        <PersonFileContext.Provider value={value}>
            {children}
            <FindPersonDialog
                open={findOpen}
                onClose={() => setFindOpen(false)}
                onSelect={openPersonFile}
            />
            <PersonFileDialog
                open={fileOpen}
                lead={lead}
                prospect={target?.prospect}
                userId={target?.userId}
                loading={leadId != null && leadQuery.isLoading && !lead}
                onClose={closePersonFile}
                readOnly={readOnly}
                renewalOffer={target?.renewalOffer}
                sequences={target?.sequences}
                pathTabs={target?.pathTabs}
                pathStage={target?.pathStage}
                onChangeSequence={target?.onChangeSequence}
                isChangingSequence={target?.isChangingSequence}
                onInviteToApply={
                    !readOnly
                        ? async (id) => {
                              const current = lead?.id === id ? lead : null
                              if (current) {
                                  setInviteLead(current)
                              }
                          }
                        : undefined
                }
                onStartScreening={!readOnly ? handleStartScreening : undefined}
                onResendScreeningInvitation={
                    !readOnly ? handleResendScreening : undefined
                }
                onSendLeaseOffer={
                    !readOnly
                        ? (current) => {
                              setOfferLead(current)
                          }
                        : undefined
                }
                onSendLeasePackage={
                    !readOnly ? handleSendLeasePackage : undefined
                }
                onScheduleTour={
                    !readOnly
                        ? (id, tourId) => {
                              if (lead?.id === id) {
                                  setTourLead(lead)
                                  setEditTourId(tourId ?? null)
                              }
                          }
                        : undefined
                }
                isStartingScreening={isStartingScreening}
                isResendingScreeningInvitation={isResendingScreeningInvitation}
                isSendingLeasePackage={isSendingLeasePackage}
                onDeleteLead={!readOnly ? handleDeleteLead : undefined}
                isDeletingLead={isDeletingLead}
            />
            <InviteToApplyDialog
                open={inviteLead != null}
                lead={inviteLead}
                onClose={() => setInviteLead(null)}
                onSuccess={refreshLead}
            />
            <ScheduleTourDialog
                open={tourLead != null}
                lead={tourLead}
                tourId={editTourId}
                onClose={() => {
                    setTourLead(null)
                    setEditTourId(null)
                }}
                onSuccess={refreshLead}
            />
            <SendLeaseOfferDialog
                open={offerRecord != null}
                record={offerRecord}
                leasingSeasonId={seasonId ?? undefined}
                onClose={() => setOfferLead(null)}
                onSent={refreshLead}
            />
        </PersonFileContext.Provider>
    )
}
